import { loadOptions } from './formset.js';
import { makeOptions, getTopIndex } from './record.js';
import { replaceIndex, REGEX } from './table.js';
import field from './field.js';
import db from './db.js';
import api from '../js/api.js';
import { fetchWarehouseLocations } from '../js/utils/location-search.js';
import filter_form from './forms/filter.js';
import filter_date from './types/filter_date.js';

const DATALIST = /(<datalist\s+id="fields"[^>]*>)(.*?)(<\/datalist>)/ims;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Valor datalist multi-empresa: `company_uuid:entity_id` (evita colisão de id entre tenants). */
const COMPANY_ENTITY_KEY = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):(\d+)$/i;

function isCompanyEntityKey(val)
{
	return COMPANY_ENTITY_KEY.test(String(val ?? '').trim());
}

/** Armazéns ativos para UI (filtros, datalists) — cadastro de armazéns não usa esta lista local. */
function warehousesActiveOnly(rows) {
	return (Array.isArray(rows) ? rows : []).filter(
		(r) => !(r.is_disabled === true || r.is_disabled === 1 || r.is_disabled === '1')
	);
}

/** Extrai company_id do formulário de filtro (resolve nick→id se necessário). Usado para escopar todos os campos filhos da empresa. */
const getCompanyIdFromFormData = async function(formData, top, companies = null) {
	companies = companies ?? await db.list('companies').catch(() => []);
	for (let i = 0; i < top; i++) {
		if (formData[`field-${i}`] === 'company_id' && formData[`value-${i}`]) {
			const val = formData[`value-${i}`];
			if (UUID_REGEX.test(String(val))) return val;
			const c = companies.find(x => (x.nick || '') === val || (x.name || '') === val || String(x.nick || '') === val || String(x.name || '') === val);
			return c?.id ?? null;
		}
	}
	return null;
};

// 'this' is not standard in these instances of formInit() and formCheck() as they point to the 'filter' object instead of a 'form' object.
// The formset creation requires a descriptor and such descriptor must be modified beforehand to point to our local 'init' and 'check' replacements.
// So these descriptor functions are bound to the 'filter' object which will then hold a pointer to the formset after its creation.
const formInit = function()
{
	// MT-596E375: inscrever para carregar opções ao trocar campo ou ao alterar Empresa (para atualizar Armazém)
	const form_node = this.formset.form.node || document.querySelector('#filter');
	if (form_node)
		form_node.addEventListener('change', (ev) =>
		{
			const fieldMatch = ev.target?.id?.match(/^field-(\d+)$/);
			if (fieldMatch)
			{
				const field = this.formset.form.getFieldBySelector(ev.target.id);
				if (field)
					updateTypes.call(this, ev, { selector: ev.target.id }, field.getValue());
			}
			else
			{
				const valueMatch = ev.target?.id?.match(/^value-(\d+)$/);
				if (valueMatch)
				{
					const idx = valueMatch[1];
					const fieldEl = this.formset.form.getFieldBySelector?.('field-' + idx);
					const fn = fieldEl?.getValue?.();
					if (fn === 'company_id')
					{
						// Empresa alterada: recarregar opções de TODOS os campos filhos (Armazém, Posição, Programa, etc.)
						const formData = this.formset?.form?.getValues?.() || {};
						const refreshAllCompanyScoped = () =>
						{
							const top = 10;
							for (let i = 0; i < top; i++)
							{
								const f = this.formset.form.getFieldBySelector?.('field-' + i);
								const fName = f?.getValue?.();
								if (fName && fName !== 'company_id' && this.fields?.some(s => s.field === fName && s.store))
									updateTypes.call(this, ev, { selector: 'field-' + i }, fName);
							}
						};
						if (this.reloadLocations)
							this.reloadLocations(formData).then(refreshAllCompanyScoped).catch(() => {});
						else
							refreshAllCompanyScoped();
					}
					else if (fn === 'warehouse_id')
					{
						// Armazém alterado: recarregar locations da API e opções de Posição
						const formData = this.formset?.form?.getValues?.() || {};
						if (this.reloadLocations)
							this.reloadLocations(formData).then(() =>
							{
								const top = 10;
								for (let i = 0; i < top; i++)
								{
									const f = this.formset.form.getFieldBySelector?.('field-' + i);
									if (f?.getValue?.() === 'location_id')
										updateTypes.call(this, ev, { selector: 'field-' + i }, 'location_id');
								}
							}).catch(() => {});
						else
						{
							const top = 10;
							for (let i = 0; i < top; i++)
							{
								const f = this.formset.form.getFieldBySelector?.('field-' + i);
								if (f?.getValue?.() === 'location_id')
									updateTypes.call(this, ev, { selector: 'field-' + i }, 'location_id');
							}
						}
					}
				}
			}
		});
	// Filtro: linhas são adicionadas só pelo botão +. autoRow (padrão de formulários
	// com itens) criava uma linha extra ao editar a última linha e copiava valores da 1ª linha.
	Object.getPrototypeOf(this.formset.form).init.call(this.formset.form, false);
};

const activeFilterIndices = function(form, indices)
{
	return indices.filter(i =>
	{
		const f = form.getFieldBySelector(`field-${i}`);
		return f && !f.isDisabled() && !f.isEmpty();
	});
};

const formCheck = function()
{
	let indices = this.formset.getEnabledRowsIndices();
	const active = activeFilterIndices(this.formset.form, indices);
	if (active.length === 0)
	{
		Object.getPrototypeOf(this.formset.form).check.call(this.formset.form, this.formset);
		return;
	}
	checkParens.call(this.formset.form, indices);
	checkValues.call(this.formset.form, active);
	checkConnectors.call(this.formset.form, active);
	Object.getPrototypeOf(this.formset.form).check.call(this.formset.form, this.formset);
};

const checkParens = function(indices)
{
	let balance = 0;
	indices.forEach(index =>
	{
		const openField = this.getFieldBySelector(`open-${index}`);
		const closeField = this.getFieldBySelector(`close-${index}`);
		balance += Number(openField?.getValue?.() ?? 0) || 0;
		balance -= Number(closeField?.getValue?.() ?? 0) || 0;
		if (balance < 0)
			throw new Error('Falta algum parêntese de abertura');
	});
	if (balance > 0)
		throw new Error('Falta algum parêntese de fechamento');
};

const checkValues = function(indices)
{
	indices.forEach(index =>
	{
		let field = this.getFieldBySelector(`field-${index}`);
		let operator = this.getFieldBySelector(`operator-${index}`);
		let value = this.getFieldBySelector(`value-${index}`);
		if (field.isDisabled() || field.isEmpty())
			return;
		if (!operator.isDisabled() && operator.isEmpty())
			setErrorState(operator, `Verificar operador da linha ${index+1}`);
		if (!value.isDisabled() && (value.isEmpty() || !value.checkValue()) && !['nu', 'nn'].includes(operator.getValue()))
			setErrorState(value, `Verificar dado da linha ${index+1}`);
	});
};

const checkConnectors = function(activeIndices)
{
	activeIndices.forEach((index, ai) =>
	{
		let field = this.getFieldBySelector(`connector-${index}`);
		let value = field.getValue();
		let is_last_active = (ai === activeIndices.length - 1);
		if ((!is_last_active && value != ',' && value != ';') || (is_last_active && value !== undefined && value != '' && value != -1))
			setErrorState(field, `Verificar conector da linha ${index+1}`);
	});
};

const setErrorState = function(field, message)
{
	field.setErrorState(true);
	throw new Error(message);
};

const fmtLocAddr = a => Array.isArray(a) ? a.join('-') : String(a ?? '');

/** ILIKE em location_address — nunca expandir location_id para warehouse_id+location_id com co/bw/ew (ILIKE '%2%' casa 2,12,20…). */
const LOCATION_TEXT_FILTER_OPS = ['co', 'bw', 'ew', 'nb', 'nd', 'nc'];

/**
 * Normaliza texto de posição para a API.
 * eq/ne: endereço completo (ex.: 2-2-F-10-2 no SESSP) — não remover o 1º segmento numérico.
 * co/bw/ew: permite prefixo explícito de armazém «N - resto» (espaços) ou «N-rest».
 * @param {string} v
 * @param {string} [operator]
 */
function stripWarehousePrefixFromLocationSearch(v, operator = 'co')
{
	const raw = String(v ?? '').trim();
	const s = normalizeLocationAddressText(raw);
	const op = String(operator || 'co').toLowerCase();
	if (op === 'eq' || op === 'ne')
		return s;
	const explicitWh = raw.match(/^(\d+)\s+-\s+(.+)$/);
	if (explicitWh)
		return normalizeLocationAddressText(explicitWh[2]);
	const m = s.match(/^(\d+)\s*-\s*(.+)$/);
	const core = m ? m[2].trim() : s;
	return normalizeLocationAddressText(core);
}

export const resolveFilterIdsToLabels = async function(data, fieldspecs)
{
	if (!data || !fieldspecs)
		return data;
	const [companies, locations] = await Promise.all([
		db.list('companies').catch(() => []),
		db.list('locations').catch(() => [])
	]);
	const top = getTopIndex(data) + 1;
	const resolved = { ...data };
	const getCompanyId = () => { for (let j = 0; j < top; j++) if (data[`field-${j}`] === 'company_id') { const v = resolved[`value-${j}`] ?? data[`value-${j}`]; return v && (UUID_REGEX.test(String(v)) ? v : companies.find(x => (x.nick || '') === v || (x.name || '') === v || String(x.nick || '') === v || String(x.name || '') === v)?.id); } return null; };
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		const val = data[`value-${i}`];
		if (!field || val == null || val === '')
			continue;
		const spec = fieldspecs.find(s => s.field === field);
		if (spec?.store === 'companies' && UUID_REGEX.test(String(val)))
		{
			const c = companies.find(x => x.id === val || String(x.id) === val);
			if (c?.[spec.labels?.[0]])
				resolved[`value-${i}`] = c[spec.labels[0]];
		}
		else if (spec?.store && spec.store !== 'companies' && spec.store !== 'locations' && spec.labels?.length
			&& (/^\d+$/.test(String(val)) || isCompanyEntityKey(val)))
		{
			// Resolução genérica id→label para stores escopados por empresa
			const scope = getCompanyId();
			let opts = await db.list(spec.store).catch(() => []);
			if (spec.store === 'warehouses')
				opts = warehousesActiveOnly(opts);
			let item = null;
			let companyNick = '';
			if (isCompanyEntityKey(val))
			{
				const m = String(val).trim().match(COMPANY_ENTITY_KEY);
				item = opts.find(o => String(o.company_id) === m[1] && String(o.id) === m[2]);
				const c = companies.find(x => String(x.id) === m[1]);
				companyNick = c?.nick || c?.name || '';
			}
			else
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && (String(o.id) === String(val)));
			if (item)
			{
				const label = spec.labels.map(k => item[k]).filter(v => v != null && String(v).trim() !== '').join(' ').trim();
				if (label)
					resolved[`value-${i}`] = companyNick ? `${companyNick} ${label}`.trim() : label;
			}
		}
		else if (spec?.store === 'locations' && field === 'location_id' && /^\d+:\d+$/.test(String(val)))
		{
			const [wh, id] = String(val).split(':');
			const loc = locations.find(l => String(l.warehouse_id) === wh && String(l.id) === id);
			if (loc)
				resolved[`value-${i}`] = `${loc.warehouse_id} - ${fmtLocAddr(loc.address)}`;
		}
	}
	return resolved;
};

export const resolveFilterLabelsToIds = async function(data, fieldspecs)
{
	if (!data || !fieldspecs)
		return data;
	let companies = await db.list('companies').catch(() => []);
	// Se companies vazio mas há company_id com label para resolver, buscar da API (db pode estar vazio antes de loadBaseData)
	const needsCompanyResolve = (() => {
		const top = getTopIndex(data) + 1;
		for (let i = 0; i < top; i++) {
			const f = data[`field-${i}`], v = data[`value-${i}`];
			if (f && v != null && v !== '' && fieldspecs.some(s => s.field === f && s.store === 'companies') && !UUID_REGEX.test(String(v).trim()))
				return true;
		}
		return false;
	})();
	if (companies.length === 0 && needsCompanyResolve && api.list_companies) {
		companies = await api.call(api.list_companies, '').catch(() => []);
		if (companies.length) db.set('companies', companies);
	}
	const locations = await db.list('locations').catch(() => []);
	const top = getTopIndex(data) + 1;
	const resolved = { ...data };
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		let val = data[`value-${i}`];
		if (!field || val == null || val === '')
			continue;
		val = String(val).trim();
		const spec = fieldspecs.find(s => s.field === field);
		if (spec?.store === 'companies' && !UUID_REGEX.test(val))
		{
			const labelKey = spec.labels?.[0] || 'nick';
			const match = (x) => (x[labelKey] || '') === val || String(x[labelKey] || '') === val || (x.name || '') === val || String(x.name || '') === val || (x.nick || '') === val || String(x.nick || '') === val;
			const c = companies.find(match);
			if (c?.id)
				resolved[`value-${i}`] = c.id;
		}
	}
	const getCompanyId = () => { for (let j = 0; j < top; j++) if (data[`field-${j}`] === 'company_id') { const v = resolved[`value-${j}`] ?? data[`value-${j}`]; return v && (UUID_REGEX.test(String(v)) ? v : companies.find(x => (x.nick || '') === v || (x.name || '') === v || String(x.nick || '') === v || String(x.name || '') === v)?.id); } return null; };
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		let val = data[`value-${i}`];
		if (!field || val == null || val === '')
			continue;
		val = String(val).trim();
		const spec = fieldspecs.find(s => s.field === field);
		const opLoc = String(data[`operator-${i}`] || 'eq').toLowerCase();
		if (spec?.store === 'locations' && field === 'location_id' && LOCATION_TEXT_FILTER_OPS.includes(opLoc))
			continue;
		// Endereço textual (CSV «2-2-F-10-2»): não resolver para wh:id — translateForLocations usa join do array.
		if (spec?.store === 'locations' && field === 'location_id' && (opLoc === 'eq' || opLoc === 'ne') && /[A-Za-z]/.test(val))
			continue;
		if (spec?.store === 'locations' && field === 'location_id' && !/^\d+:\d+$/.test(val))
		{
			// Label "warehouse_id - address" ou apenas "address"
			const match = val.match(/^(\d+)\s*-\s*(.+)$/);
			const wh = match ? match[1] : null;
			const addrPart = match ? match[2].trim() : val;
			const addrNorm = addrPart.replace(/\s+/g, '-').toUpperCase();
			const scopeCompany = getCompanyId();
			const loc = locations.find(l =>
			{
				const a = fmtLocAddr(l.address ?? l.id);
				const addrMatch = a.replace(/\s+/g, '-').toUpperCase() === addrNorm || a.toUpperCase().includes(addrNorm);
				if (!addrMatch) return false;
				if (scopeCompany && String(l.company_id) !== String(scopeCompany)) return false;
				if (wh && String(l.warehouse_id) !== wh) return false;
				return true;
			});
			if (loc)
				resolved[`value-${i}`] = `${loc.warehouse_id}:${loc.id}`;
		}
		else if (spec?.store === 'customers' && field === 'customer_id')
		{
			const scope = getCompanyId();
			const opts = await db.list('customers').catch(() => []);
			const digits = val.replace(/\D/g, '');
			const normDoc = (d) => (d == null ? '' : String(d).replace(/\D/g, ''));
			let item = null;
			if (digits.length === 14)
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && normDoc(o.cnpj) === digits);
			if (!item && digits.length === 11)
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && normDoc(o.cpf) === digits);
			if (item?.id != null)
				resolved[`value-${i}`] = item.id;
			else if (!/^\d+$/.test(val))
			{
				const labelKey = spec.labels[0];
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && ((o[labelKey] || '') === val || String(o[labelKey] || '') === val));
				if (item?.id != null)
					resolved[`value-${i}`] = item.id;
			}
		}
		else if (spec?.store && spec.store !== 'companies' && isCompanyEntityKey(val))
		{
			resolved[`value-${i}`] = val.trim();
		}
		else if (spec?.store && spec.store !== 'companies' && spec.labels?.length && !/^\d+$/.test(val) && !/^\d+:\d+$/.test(val) && field !== 'classes' && field !== 'categoria')
		{
			// Resolução genérica para stores escopados por empresa (warehouses, programs, units, products, etc.)
			const scope = getCompanyId();
			let opts = await db.list(spec.store).catch(() => []);
			if (spec.store === 'warehouses')
				opts = warehousesActiveOnly(opts);
			const inScope = (o) => !scope || String(o.company_id) === String(scope);
			const norm = (t) => String(t ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
			const nVal = norm(val);
			const joinLabel = (o) => spec.labels.map(k => o[k]).filter(v => v != null && String(v).trim() !== '').join(' ').trim();
			let item = opts.find(o => inScope(o) && joinLabel(o) === val);
			if (!item)
				item = opts.find(o => inScope(o) && spec.labels.some(k => String(o[k] ?? '') === val));
			// nick/name case-insensitive (ex.: Fabricante "ACCORD" / "accord")
			if (!item && nVal)
			{
				const exactNorm = opts.filter(o => inScope(o) && (
					spec.labels.some(k => norm(o[k]) === nVal) || norm(joinLabel(o)) === nVal
				));
				if (exactNorm.length === 1)
					item = exactNorm[0];
			}
			if (!item && (spec.store === 'suppliers' || spec.store === 'customers' || spec.store === 'carriers'))
			{
				const digits = val.replace(/\D/g, '');
				const normDoc = (d) => (d == null ? '' : String(d).replace(/\D/g, ''));
				if (digits.length === 14 || digits.length === 11)
				{
					const docMatches = opts.filter(o => inScope(o) && (normDoc(o.cnpj) === digits || normDoc(o.cpf) === digits));
					if (docMatches.length === 1)
						item = docMatches[0];
				}
			}
			if (item?.id != null)
			{
				// Sem Empresa no filtro: guardar company:id para não colidir entre tenants
				resolved[`value-${i}`] = (!scope && item.company_id)
					? `${item.company_id}:${item.id}`
					: item.id;
			}
		}
	}
	return resolved;
};

/**
 * Após resolver labels→ids: impede aplicar texto solto em campo numérico/id
 * (ex.: id*eq*accord → Postgres 42703).
 */
export function validateResolvedFilterValues(data, fieldspecs)
{
	if (!data || !fieldspecs)
		return;
	const top = getTopIndex(data) + 1;
	const isNullOp = (op) => op === 'nu' || op === 'nn';
	const textOps = new Set(['co', 'bw', 'ew', 'cc', 'cb', 'ce', 'nc', 'nb', 'nd', 'si']);
	const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		const op = String(data[`operator-${i}`] || '').toLowerCase();
		const raw = data[`value-${i}`];
		if (!field || isNullOp(op))
			continue;
		if (raw == null || String(raw).trim() === '')
			continue;
		const val = String(raw).trim();
		const spec = fieldspecs.find(s => s.field === field);
		const label = spec?.label || field;
		if (val === '-1' || val === '-2')
			throw new Error(`Valor inválido em "${label}". Escolha um item da lista.`);
		if (!spec)
			continue;
		if (textOps.has(op))
			continue;
		if (spec.store === 'companies' || field === 'company_id')
		{
			if (!UUID_RE.test(val))
				throw new Error(`Empresa não reconhecida: "${val}". Selecione na lista.`);
			continue;
		}
		// users.id é UUID (ex.: SIALBOT b07c4f2a-…); não exigir id numérico (#MT-8678543).
		const isUserField = spec.store === 'users'
			|| field === 'user_id'
			|| field === 'created_by'
			|| field === 'updated_by'
			|| field === 'recorded_by'
			|| field === 'manager_user_id';
		if (isUserField)
		{
			if (!UUID_RE.test(val))
				throw new Error(`Usuário não reconhecido: "${val}". Selecione na lista.`);
			continue;
		}
		if (spec.store === 'locations' || field === 'location_id')
		{
			if (!/^\d+:\d+$/.test(val) && !/^\d+$/.test(val) && !/[A-Za-z]/.test(val))
				throw new Error(`Posição inválida: "${val}".`);
			if (/[A-Za-z]/.test(val) && !/^\d+:\d+$/.test(val))
				continue; // endereço textual (eq/ne tratados no translate)
			continue;
		}
		const expectsId = Boolean(spec.store)
			|| field === 'id'
			|| /_id$/.test(field)
			|| spec.types?.some(t => t && typeof t.dp === 'number' && t.dp === 0);
		if (expectsId && !/^\d+$/.test(val) && !/^\d+:\d+$/.test(val) && !isCompanyEntityKey(val))
		{
			throw new Error(
				`Valor "${val}" inválido para ${label}. Selecione o item na lista (o filtro precisa do código interno, não de texto solto).`
			);
		}
	}
}

/** Resolve nome técnico do campo (field) a partir de código ou rótulo da lista. */
export function resolveFilterFieldName(fieldDefs, raw)
{
	const s = String(raw ?? '').trim();
	if (!s || !Array.isArray(fieldDefs))
		return s;
	const byField = fieldDefs.find(f => f.field === s);
	if (byField)
		return byField.field;
	const norm = (t) => String(t ?? '').trim().toLowerCase();
	const n = norm(s);
	const byLabel = fieldDefs.find(f => f.label && norm(f.label) === n);
	return byLabel?.field || s;
}

/** No filtro, campos date/datetime usam só data + calendário (sem horário). */
function filterValueTypesForFieldspec(fieldspec)
{
	const raw = fieldspec?.types || [];
	if (raw.some(t => t?.kind === 'date' || t?.kind === 'datetime'))
		return [filter_date()];
	return raw;
}

function fieldspecIsDatetime(fieldspecs, fieldName)
{
	const spec = fieldspecs?.find(s => s.field === fieldName);
	return spec?.types?.some(t => t?.kind === 'datetime');
}

function pad2(n)
{
	return String(n).padStart(2, '0');
}

function parseFilterDateOnly(value)
{
	const s = String(value ?? '').trim();
	if (!s)
		return null;
	let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m)
		return { y: +m[1], m: +m[2], d: +m[3] };
	m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
	if (m)
		return { y: +m[3], m: +m[2], d: +m[1] };
	return null;
}

function filterValueHasTimeComponent(value)
{
	const s = String(value ?? '').trim();
	return /\d{1,2}:\d{2}/.test(s);
}

function dayStartLocal(parts)
{
	return `${parts.y}-${pad2(parts.m)}-${pad2(parts.d)}T00:00:00`;
}

function nextDayStartLocal(parts)
{
	const dt = new Date(parts.y, parts.m - 1, parts.d);
	dt.setDate(dt.getDate() + 1);
	return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}T00:00:00`;
}

/** Converte eq/ge/le… em campo timestamp para intervalo do dia quando o valor é só data. */
function expandDatetimeFilterClause(field, operator, value, open, close)
{
	const op = String(operator || '').toLowerCase();
	if (op === 'nu' || op === 'nn')
		return null;
	if (filterValueHasTimeComponent(value))
		return null;
	const parts = parseFilterDateOnly(value);
	if (!parts)
		return null;
	const start = dayStartLocal(parts);
	const next = nextDayStartLocal(parts);
	switch (op)
	{
		case 'eq':
			return `${open}${field}*ge*${start},${field}*lt*${next}${close}`;
		case 'ne':
			return `${open}${field}*lt*${start};${field}*ge*${next}${close}`;
		case 'ge':
			return `${open}${field}*ge*${start}${close}`;
		case 'gt':
			return `${open}${field}*ge*${next}${close}`;
		case 'le':
			return `${open}${field}*lt*${next}${close}`;
		case 'lt':
			return `${open}${field}*lt*${start}${close}`;
		default:
			return null;
	}
}

export const updateTypes = async function(ev, publisher, field_name)
{
	field_name = resolveFilterFieldName(this.fields, field_name);
	let basespec = filter_form.rowFieldSpecs?.find(f => f.properties.selector === '#value-0')
		|| { properties: { selector: '#value-0' }, types: [] };
	let fieldspec = this.fields.find(entry => entry.field == field_name);	// 'field_name' may be -1 when moving between rows
	let typed_field = field.create(basespec.properties, basespec.types.concat(filterValueTypesForFieldspec(fieldspec)));
	const pubSel = String(publisher?.selector || '').replace(/^#/, '');
	const pubMatch = pubSel.match(REGEX);
	if (!pubMatch)
		return;
	let index = pubMatch[3];
	let current_field = this.formset.form.getFieldBySelector(replaceIndex('value-0', index));
	if (!current_field)
		return;
	Object.assign(typed_field, { selector: current_field.selector, form: this.formset.form });
	current_field.releaseEvents();
	typed_field.releaseEvents();
	let node = typed_field.queryNode();
	const formScope = this.formset?.form?.selector;
	const valueTypes = filterValueTypesForFieldspec(fieldspec);
	const usesDateFilter = valueTypes.length === 1 && valueTypes[0]?.kind === 'date';
	if (usesDateFilter && node)
	{
		node.removeAttribute('placeholder');
		node.setAttribute('title', 'Selecione a data');
	}
	else
	{
		if (fieldspec?.placeholder && node)
			node.setAttribute('placeholder', fieldspec.placeholder);
		if (fieldspec?.title && node)
			node.setAttribute('title', fieldspec.title);
	}
	if (fieldspec?.store)
	{
		let options = await db.list(fieldspec.store);
		if (fieldspec.store === 'warehouses')
			options = warehousesActiveOnly(options);
		// MT-596E375: filtrar por company_id/warehouse_id, ordenar por address, value = warehouse_id:location_id
		if (fieldspec.store === 'locations' && (fieldspec.field === 'location_id' || field_name === 'location_id'))
		{
			const formData = this.formset?.form?.getValues?.() || {};
			await loadLocationFilterDatalist(typed_field, formScope, formData, String(node?.value ?? '').trim());
			if (node)
				bindLocationFilterLiveSearch(node, typed_field, formScope, this.formset);
		}
		else if (fieldspec.store !== 'companies' && fieldspec.store !== 'locations' && options.length > 0
			&& (fieldspec.store === 'warehouses' || options[0].company_id != null))
		{
			// Stores escopados por empresa: warehouses, programs, units, classes, products, etc.
			const formData = this.formset?.form?.getValues?.() || {};
			const top = getTopIndex(formData) + 1;
			const companyId = await getCompanyIdFromFormData(formData, top);
			if (companyId)
				options = options.filter(o => String(o.company_id) === String(companyId));
			// Inativos (produtos, etc.): armazém já veio filtrado em warehousesActiveOnly no início.
			if (fieldspec.store !== 'warehouses' && options.length > 0 && options[0].is_disabled != null)
				options = options.filter(o => !o.is_disabled);
			if (!companyId && options.length > 0 && options[0].company_id != null)
			{
				// Sem Empresa: value = company_id:id (ids colidem entre tenants — ex. id=4 ACCORD vs ANGELUS)
				const companies = await db.list('companies').catch(() => []);
				const nickBy = Object.fromEntries(companies.map(c => [String(c.id), c.nick || c.name || '']));
				const labels = fieldspec.labels || ['name'];
				const mapped = options.map(o =>
				{
					const parts = labels.map(k => o[k]).filter(v => v != null && String(v).trim() !== '');
					const cn = nickBy[String(o.company_id)] || '';
					return {
						value: `${o.company_id}:${o.id}`,
						label: [cn, ...parts].filter(Boolean).join(' ').trim()
					};
				});
				loadOptions(typed_field.selector, mapped, 'value', ['label'], undefined, formScope);
			}
			else
				loadOptions(typed_field.selector, options, fieldspec.value || 'id', fieldspec.labels, fieldspec.separator, formScope);
		}
		else
		{
			// Empresas: usar id como value (enviar UUID à API), nick como label (exibição)
			if (fieldspec.store === 'companies' && fieldspec.labels?.[0])
			{
				const labelKey = fieldspec.labels[0];
				const seen = new Set();
				options = options.filter(o => {
					const k = o.id ?? '';
					if (seen.has(k)) return false;
					seen.add(k);
					return true;
				});
				loadOptions(typed_field.selector, options, 'id', [labelKey], undefined, formScope);
			}
			else
				loadOptions(typed_field.selector, options, fieldspec.value, fieldspec.labels, fieldspec.separator, formScope);
		}
	}
	else
	{
		node?.removeAttribute('list');
	}
	this.formset.form.insertOrUpdateField(typed_field);
	typed_field.captureEvents();
	typed_field.refresh();
};

const LOCATIONS_FIELDS = ['company_id', 'warehouse_id', 'location_id'];

const SCOPE_FIELDS = ['company_id', 'warehouse_id'];

function normalizeLocationAddressText(raw)
{
	return String(raw ?? '').trim()
		.replace(/\s*-\s*/g, '-')
		.replace(/\s*,\s*/g, '-')
		.replace(/\s+/g, '-');
}

/** Texto incompleto ao digitar (ex.: «2-m-» / «m-» / «M») — não usar match exato cb+ce. */
function isPartialLocationAddress(addressJoin)
{
	const a = String(addressJoin ?? '').trim();
	if (!a)
		return true;
	if (/[-]$/.test(a))
		return true;
	// Só a letra do corredor («M») enquanto digita «2-M…»
	if (/^[A-Za-z]$/.test(a))
		return true;
	return false;
}

/** Rótulo «2 - 2-F-10-2» / CSV «2-2-F-10-2» / parcial «2-m-» → armazém + trecho do endereço.
 *  «6-B-4-14» (prefixo legado no array, armazém físico outro) → join completo, sem warehouse_id. */
function parseCombinedLocationAddress(raw)
{
	const s = normalizeLocationAddressText(raw);
	const m = s.match(/^(\d+)-(\d+-[A-Za-z].*)$/);
	if (m)
		return { warehouseId: m[1], addressJoin: m[2] };
	const p = s.match(/^(\d+)-(.+)$/);
	if (p)
	{
		const wh = p[1];
		const rest = p[2];
		if (/^\d+-/.test(rest))
			return { warehouseId: wh, addressJoin: rest };
		if (/^[A-Za-z]/.test(rest))
		{
			// Legado completo «6-B-4-14»: letra + segmentos numéricos, sem hífen final.
			if (!isPartialLocationAddress(rest) && /^[A-Za-z](-\d+)+$/.test(rest))
				return { addressJoin: s };
			// Prefixo de armazém + trecho («2-m-», «2-M», «2-M-1-…» incompleto)
			return { warehouseId: wh, addressJoin: rest };
		}
		return { warehouseId: wh, addressJoin: rest };
	}
	return { addressJoin: s };
}

/** Endereço «2-2-F-10-2» já traz armazém no prefixo; legado «6-B-4-14» não. */
function combinedLocationAddressImpliesWarehouse(raw)
{
	const s = normalizeLocationAddressText(raw);
	return /^\d+-\d+-[A-Za-z]/.test(s);
}

function mayInferWarehouseFromAddress(search, warehouseId, explicitWarehouseOnly)
{
	if (!warehouseId)
		return false;
	if (!explicitWarehouseOnly)
		return true;
	return combinedLocationAddressImpliesWarehouse(search);
}

function escapeLocationFilterToken(s)
{
	return String(s ?? '').replace(/[*,]/g, '');
}

/** Endereços SESSP usam letras maiúsculas (M-1-2); normaliza digitação «2-m-». */
function uppercaseLocationAddressLetters(s)
{
	return String(s ?? '').replace(/[a-z]/g, (ch) => ch.toUpperCase());
}

function filterLocationOptionsBySearch(options, searchTerm)
{
	const term = normalizeLocationAddressText(searchTerm);
	if (!term)
		return options;
	const { warehouseId: whFromTerm, addressJoin } = parseCombinedLocationAddress(term);
	const termUpper = term.toUpperCase();
	const fragUpper = uppercaseLocationAddressLetters(addressJoin || term);
	return options.filter((o) =>
	{
		if (whFromTerm && String(o.warehouse_id) !== String(whFromTerm))
			return false;
		const label = (o._label || '').toUpperCase();
		const addr = (o._addrSort || '').toUpperCase();
		return label.includes(termUpper) || addr.includes(fragUpper);
	});
}

async function resolveFilterLocationScope(formData, topIdx)
{
	let companyId = null;
	let warehouseId = null;
	const [companies, warehousesRaw] = await Promise.all([
		db.list('companies').catch(() => []),
		db.list('warehouses').catch(() => []),
	]);
	const warehouses = warehousesActiveOnly(warehousesRaw);
	for (let i = 0; i < topIdx; i++)
	{
		if (formData[`field-${i}`] === 'company_id' && formData[`value-${i}`])
		{
			const val = formData[`value-${i}`];
			companyId = UUID_REGEX.test(String(val)) ? val : null;
			if (!companyId)
			{
				const c = companies.find(x => (x.nick || '') === val || (x.name || '') === val || String(x.nick || '') === val || String(x.name || '') === val);
				companyId = c?.id;
			}
		}
		else if (formData[`field-${i}`] === 'warehouse_id' && formData[`value-${i}`] != null && formData[`value-${i}`] !== '')
		{
			const wval = formData[`value-${i}`];
			warehouseId = /^\d+$/.test(String(wval)) ? (typeof wval === 'number' ? wval : parseInt(wval, 10)) : null;
			if (warehouseId == null && companyId)
			{
				const wh = warehouses.find(w => String(w.company_id) === String(companyId) && ((w.name || '') === wval || String(w.name || '') === wval));
				if (wh)
					warehouseId = wh.id;
			}
		}
	}
	return { companyId, warehouseId, companies, warehouses };
}

function mapLocationFilterOptions(locations, warehouses)
{
	const activeWhKeys = new Set(warehouses.map((w) => `${w.company_id}:${w.id}`));
	let options = (locations || []).map((loc) =>
	{
		const addr = fmtLocAddr(loc.address ?? loc.id);
		return {
			...loc,
			_wh_loc: `${loc.warehouse_id}:${loc.id}`,
			_label: `${loc.warehouse_id} - ${addr}`,
			_addrSort: addr,
		};
	});
	if (activeWhKeys.size > 0)
		options = options.filter((l) => activeWhKeys.has(`${l.company_id}:${l.warehouse_id}`));
	options.sort((a, b) => (a._addrSort || '').localeCompare(b._addrSort || '', undefined, { numeric: true }));
	return options;
}

async function loadLocationFilterDatalist(typed_field, formScope, formData, searchTerm = '')
{
	const topIdx = getTopIndex(formData) + 1;
	const { companyId, warehouseId, warehouses } = await resolveFilterLocationScope(formData, topIdx);
	const typed = String(searchTerm ?? '').trim();
	let options = [];

	// Com empresa+armazém e texto: busca na API pelo trecho do endereço (não o prefixo «2-» inteiro).
	if (companyId && warehouseId != null && typed)
	{
		const parsed = parseCombinedLocationAddress(typed);
		const wh = parsed.warehouseId && String(parsed.warehouseId) === String(warehouseId)
			? Number(parsed.warehouseId)
			: warehouseId;
		const frag = uppercaseLocationAddressLetters(parsed.addressJoin || typed);
		const rows = await fetchWarehouseLocations(companyId, wh, {
			search: frag,
			limit: 80,
			silent: true,
		});
		options = mapLocationFilterOptions(rows, warehouses);
	}
	else
	{
		// Sem texto (ou sem escopo): lista local como antes — datalist nativo + filtro ao digitar.
		let idbRows = await db.list('locations').catch(() => []);
		options = mapLocationFilterOptions(idbRows, warehouses);
		if (companyId)
			options = options.filter((l) => String(l.company_id) === String(companyId));
		if (warehouseId != null)
			options = options.filter((l) => Number(l.warehouse_id) === Number(warehouseId));
		if (typed)
			options = filterLocationOptionsBySearch(options, typed);
	}
	loadOptions(typed_field.selector, options, '_wh_loc', ['_label'], undefined, formScope);
}

function bindLocationFilterLiveSearch(node, typed_field, formScope, formset)
{
	if (!node || node.dataset.locFilterLive === '1')
		return;
	node.dataset.locFilterLive = '1';
	let timer = null;
	node.addEventListener('input', (ev) =>
	{
		if (ev.inputType === 'insertReplacementText')
			return;
		clearTimeout(timer);
		const typed = String(ev.target?.value ?? '').trim();
		timer = setTimeout(async () =>
		{
			try
			{
				const formData = formset?.form?.getValues?.() || {};
				await loadLocationFilterDatalist(typed_field, formScope, formData, typed);
				if (node && typed)
					node.value = typed;
			}
			catch (err)
			{
				console.error('location filter live search', err);
			}
		}, 280);
	});
}

/**
 * Monta cláusula de filtro por endereço de posição.
 * Catálogo `/locations` usa `address` (TEXT[]); estoque/movimentações usam `location_address`.
 * @param {string} locationTextField
 * @param {{ explicitWarehouseOnly?: boolean }} [opts]
 */
function appendLocationAddressClause(open, operator, value, close, locationTextField, splitWarehouse = true, opts = {})
{
	const explicitWarehouseOnly = opts.explicitWarehouseOnly === true;
	const opLower = String(operator || 'eq').toLowerCase();
	const search = splitWarehouse
		? stripWarehousePrefixFromLocationSearch(value, opLower)
		: normalizeLocationAddressText(value);
	if (locationTextField === 'address' && (opLower === 'eq' || opLower === 'ne'))
	{
		if (!splitWarehouse)
		{
			const addr = uppercaseLocationAddressLetters(escapeLocationFilterToken(search));
			if (opLower === 'eq' && isPartialLocationAddress(addr))
				return `${open}address*cc*${addr}${close}`;
			const addrClauses = `address*cb*${addr},address*ce*${addr}`;
			return `${open}${addrClauses}${close}`;
		}
		const { warehouseId, addressJoin } = parseCombinedLocationAddress(search);
		if (opLower === 'eq' && isPartialLocationAddress(addressJoin))
		{
			const term = uppercaseLocationAddressLetters(escapeLocationFilterToken(addressJoin));
			const addrClause = `address*cc*${term}`;
			// Parcial com armazém já parseado («2-m-») — confiar no warehouseId mesmo com explicitWarehouseOnly.
			if (warehouseId)
				return `${open}warehouse_id*eq*${warehouseId},${addrClause}${close}`;
			return `${open}${addrClause}${close}`;
		}
		const addrClauses = `address*cb*${addressJoin},address*ce*${addressJoin}`;
		if (mayInferWarehouseFromAddress(search, warehouseId, explicitWarehouseOnly))
			return `${open}warehouse_id*${operator}*${warehouseId},${addrClauses}${close}`;
		return `${open}${addrClauses}${close}`;
	}
	return `${open}${locationTextField}*${operator}*${search}${close}`;
}

/** OR de endereços no mesmo armazém → address*in* (evita URL > buffer uWSGI 4096).
 * Aceita ramos com ou sem warehouse_id*eq*N, e ignora ramo órfão só de warehouse_id.
 * Também: warehouse_id*eq*N,(addr;addr;…) — forma AND(escopo, OR).
 */
function compactLocationAddressOrExpression(expr)
{
	if (!expr || !String(expr).includes(';'))
		return expr;
	let raw = String(expr).trim();
	const scoped = raw.match(/^warehouse_id\*eq\*(\d+),\((.+)\)$/i);
	if (scoped)
	{
		const collapsedInner = compactLocationAddressOrExpression(`(${scoped[2]})`);
		if (collapsedInner.startsWith('(warehouse_id*eq*'))
			return collapsedInner;
		const innerOnly = compactAddressBranches(scoped[2], scoped[1]);
		if (innerOnly)
			return innerOnly;
		return expr;
	}
	if (raw.startsWith('(') && raw.endsWith(')'))
		raw = raw.slice(1, -1);
	return compactAddressBranches(raw, null) || expr;
}

function compactAddressBranches(inner, forcedWh)
{
	const parts = String(inner).split(';').map(p => p.trim()).filter(Boolean);
	if (parts.length < 2)
		return null;
	const withWhRe = /^\(?warehouse_id\*eq\*(\d+),address\*cb\*([^,]+),address\*ce\*([^,)]+)\)?$/i;
	const addrOnlyRe = /^\(?address\*cb\*([^,]+),address\*ce\*([^,)]+)\)?$/i;
	const whOnlyRe = /^\(?warehouse_id\*eq\*(\d+)\)?$/i;
	let warehouseId = forcedWh;
	const addresses = [];
	for (const part of parts)
	{
		let m = part.match(withWhRe);
		if (m)
		{
			if (m[2] !== m[3])
				return null;
			if (warehouseId == null)
				warehouseId = m[1];
			else if (String(warehouseId) !== String(m[1]))
				return null;
			addresses.push(m[2]);
			continue;
		}
		m = part.match(addrOnlyRe);
		if (m)
		{
			if (m[1] !== m[2])
				return null;
			addresses.push(m[1]);
			continue;
		}
		m = part.match(whOnlyRe);
		if (m)
		{
			if (warehouseId == null)
				warehouseId = m[1];
			else if (String(warehouseId) !== String(m[1]))
				return null;
			continue;
		}
		return null;
	}
	if (addresses.length < 2 || warehouseId == null)
		return null;
	const uniq = [...new Set(addresses)];
	return `(warehouse_id*eq*${warehouseId},address*in*${uniq.join('@')})`;
}

/** Converte valor digitado na máscara pt-BR (ex.: 2.530) para literal numérico da API. */
function normalizeFilterValueForFieldspec(field, raw, fieldspecs)
{
	if (raw == null || raw === '' || !fieldspecs)
		return raw;
	const spec = fieldspecs.find(s => s.field === field);
	const numType = spec?.types?.find(t => t && typeof t.dp === 'number');
	if (!numType)
		return raw;
	let s = String(raw).trim().replace(/\s/g, '');
	const dp = numType.dp;
	if (dp === 0)
	{
		if (/,/.test(s))
			return raw;
		const cleaned = s.replace(/\./g, '');
		return /^\d+$/.test(cleaned) ? cleaned : raw;
	}
	if (/,/.test(s))
		return s.replace(/\./g, '').replace(',', '.');
	if (/^\d{1,3}(\.\d{3})+$/.test(s.replace(/^-/, '')))
		return s.replace(/\./g, '');
	return raw;
}

const translate = function(data, allowedFields = null, fieldspecs = null, locationTextField = 'location_address', translateOpts = {})
{
	const locClauseOpts = { explicitWarehouseOnly: translateOpts.explicitWarehouseOnly === true };
	let expression = '';
	let top_index = getTopIndex(data) + 1;
	let wrapOrGroup = false;
	const isNullOp = (op) => op === 'nu' || op === 'nn';
	const willOutput = (i) =>
	{
		const f = data[`field-${i}`];
		const v = data[`value-${i}`];
		const op = data[`operator-${i}`];
		if (!f)
			return false;
		if (!isNullOp(op) && (v == null || v === ''))
			return false;
		if (allowedFields && !allowedFields.includes(f))
			return false;
		if (f === 'location_id')
			return typeof v === 'string' && v.trim() !== '';
		return true;
	};

	for (let index = 0; index < top_index; index++)
	{
		if (!willOutput(index))
			continue;

		let open = data[`open-${index}`] == 1 ? '(' : '';
		let field = data[`field-${index}`];
		let operator = data[`operator-${index}`];
		let value = isNullOp(operator)
			? (data[`value-${index}`] != null && String(data[`value-${index}`]).trim() !== ''
				? String(data[`value-${index}`]).trim()
				: '0')
			: normalizeFilterValueForFieldspec(field, data[`value-${index}`], fieldspecs);
		// Classes (agregada) e categoria: "igual a" funciona como "contém" (ILIKE).
		if (field === 'classes' || field === 'categoria')
		{
			if (operator === 'eq') operator = 'co';
			else if (operator === 'ne') operator = 'nc';
		}
		let close = data[`close-${index}`] == 1 ? ')' : '';
		let connector = data[`connector-${index}`];
		const hasNext = () => { for (let j = index + 1; j < top_index; j++) if (willOutput(j)) return true; return false; };
		const nextActiveIndex = () => { for (let j = index + 1; j < top_index; j++) if (willOutput(j)) return j; return -1; };
		const restContainsOr = () =>
		{
			for (let j = index; j < top_index - 1; j++)
			{
				if (!willOutput(j))
					continue;
				if (String(data[`connector-${j}`] ?? '').trim() === ';')
					return true;
			}
			return false;
		};

		// Posição: texto (co/bw/ew…) → location_address; igual/diferente com par wh:id → dois campos inteiros; senão → texto no endereço (rótulo não resolvido).
		if (field === 'location_id')
		{
			const opLower = String(operator || 'eq').toLowerCase();
			if (/^\d+:\d+$/.test(String(value).trim()))
			{
				const idField = locationTextField === 'address' ? 'id' : 'location_id';
				let [wh, loc] = String(value).split(':');
				expression += `${open}warehouse_id*${operator}*${wh},${idField}*${operator}*${loc}${close}`;
			}
			else
				expression += appendLocationAddressClause(open, operator, value, close, locationTextField, true, locClauseOpts);
		}
		else if (field === 'address')
			expression += appendLocationAddressClause(open, operator, value, close, 'address', false, locClauseOpts);
		else if (isCompanyEntityKey(value) && (field === 'id' || /_id$/.test(field)))
		{
			// Multi-empresa no datalist: company_uuid:entity_id → company_id + campo
			const m = String(value).trim().match(COMPANY_ENTITY_KEY);
			expression += `${open}company_id*eq*${m[1]},${field}*${operator}*${m[2]}${close}`;
		}
		else if (fieldspecs && fieldspecIsDatetime(fieldspecs, field))
		{
			const expanded = expandDatetimeFilterClause(field, operator, value, open, close);
			expression += expanded ?? `${open}${field}*${operator}*${value}${close}`;
		}
		else
			expression += `${open}${field}*${operator}*${value}${close}`;

		if (hasNext())
		{
			// Conector vazio entre linhas ativas → AND (evita colar cláusulas e quebrar filtro na API).
			let conn = (connector != null && connector !== '' && connector !== -1 && connector !== '-1')
				? connector
				: ',';
			// Posição "igual a" repetida em linhas consecutivas é seleção de múltiplas posições.
			// Força OR para evitar expressão impossível (A e B simultaneamente na mesma linha).
			const nextIdx = nextActiveIndex();
			if (
				nextIdx >= 0
				&& field === 'location_id'
				&& String(operator || '').toLowerCase() === 'eq'
				&& data[`field-${nextIdx}`] === 'location_id'
				&& String(data[`operator-${nextIdx}`] || '').toLowerCase() === 'eq'
				&& conn === ','
			)
				conn = ';';
			// Empresa/Armazém seguido de cláusulas com "Ou": forçar AND(escopo, OR(resto))
			// Se a próxima linha já traz open-1 (UX de grupos), não duplicar '(' — a API aceita só um nível.
			const nextRowHasOpen = () =>
			{
				for (let j = index + 1; j < top_index; j++)
				{
					if (willOutput(j) && data[`open-${j}`] == 1)
						return true;
				}
				return false;
			};
			if (SCOPE_FIELDS.includes(field) && restContainsOr() && !wrapOrGroup && !nextRowHasOpen())
			{
				const connAfter = String(data[`connector-${index}`] ?? '').trim();
				// Só AND(… OR …) quando o próximo vínculo é E. Se já é OU (;), não inserir ",(".
				if (connAfter === ',')
				{
					expression += ',(';
					wrapOrGroup = true;
				}
				else
					expression += conn;
			}
			else
				expression += conn;
		}
		else if (wrapOrGroup)
		{
			expression += ')';
			wrapOrGroup = false;
		}
	}
	if (wrapOrGroup)
		expression += ')';
	return expression;
};

const filter = {

	fields: null,
	formset: null,

	init(fields)	// [{ field: '', label: '', types: [], usage: '' }, ...]
	{
		this.fields = fields;
		const filterable = (fields || []).filter((f) => !f.computed);
		let options = makeOptions(filterable, 'field', ['label']);
		filter_form.html = filter_form.html.replace(DATALIST, '$1' + options + '$3');
	},

	edit(callback, current_data, options = {})
	{
		if (this._uiController?.destroy)
		{
			try { this._uiController.destroy(); } catch (_) { /* modal anterior */ }
		}
		else if (this.formset?.destroy)
		{
			try { this.formset.destroy(); } catch (_) { /* legado */ }
			this.formset = null;
		}
		this.reloadLocations = options.reloadLocations || null;
		return import('./filter_ui.js').then(({ openFilterModal }) =>
			openFilterModal(this, callback, current_data, options));
	},

};

export const validateLocationsFilterWarehouseRequired = function(data, fieldspecs = null)
{
	if (!data)
		return;
	const top = getTopIndex(data) + 1;
	let hasWarehouse = false;
	let needsWarehouse = false;
	const isNullOp = (op) => op === 'nu' || op === 'nn';
	for (let i = 0; i < top; i++)
	{
		const f = data[`field-${i}`];
		const v = data[`value-${i}`];
		const op = String(data[`operator-${i}`] || '').toLowerCase();
		if (!f)
			continue;
		if (f === 'warehouse_id' && v != null && String(v).trim() !== '')
			hasWarehouse = true;
		if ((f === 'address' || f === 'location_id') && !isNullOp(op) && v != null && String(v).trim() !== '')
		{
			const text = String(v).trim();
			if (f === 'location_id' && /^\d+:\d+$/.test(text))
				hasWarehouse = true;
			else if (combinedLocationAddressImpliesWarehouse(text))
				hasWarehouse = true;
			else
				needsWarehouse = true;
		}
	}
	if (needsWarehouse && !hasWarehouse)
		throw new Error('Informe o armazém antes de filtrar por posição ou endereço.');
};

/**
 * @param {Record<string, unknown>} data
 * @param {{ field: string }[] | null} [fieldspecs]
 * @param {{ strict?: boolean }} [options] strict=true valida armazém (aplicar filtro / API); preview usa false.
 */
export const translateForLocations = (data, fieldspecs = null, options = {}) =>
{
	if (options.strict)
		validateLocationsFilterWarehouseRequired(data, fieldspecs);
	const allowed = fieldspecs?.length
		? fieldspecs.map(fs => fs.field).filter(Boolean)
		: LOCATIONS_FIELDS;
	return compactLocationAddressOrExpression(
		translate(data, allowed, fieldspecs, 'address', { explicitWarehouseOnly: true }),
	);
};

export const __filterTestHooks = {
	filterLocationOptionsBySearch,
	normalizeLocationAddressText,
	parseCombinedLocationAddress,
	uppercaseLocationAddressLetters,
	validateLocationsFilterWarehouseRequired,
	combinedLocationAddressImpliesWarehouse,
	isPartialLocationAddress,
};

export { filter as default, translate };

